import { useEffect, useMemo, useState } from 'react';
import { CircleNotch, LockSimple, WarningCircle } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Switch } from '@/components/ui-kit/switch';
import { Textarea } from '@/components/ui-kit/textarea';
import { useToast } from '@/hooks/use-toast';
import { HttpError } from '@/lib/https';
import { cn } from '@/lib/utils';
import {
  useGetLegalFooterSettings,
  useUpdateLegalFooterSettings,
} from '../hooks/use-compliance-settings';
import type { LegalFooterSettings } from '../types/compliance-settings.types';

export type LegalFooter = LegalFooterSettings;

export const INITIAL_FOOTER: LegalFooter = {
  companyName: '',
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  city: '',
  country: '',
  extraText: '',
  includeUnsubscribeLink: true,
  includePreferencesLink: false,
};

export type FooterErrors = Partial<Record<keyof LegalFooter, string>>;

export const validateLegalFooter = (f: LegalFooter): FooterErrors => {
  const errors: FooterErrors = {};
  const company = (f.companyName ?? f.legalName ?? '').trim();
  const address = (f.addressLine1 ?? f.postalAddress ?? '').trim();
  const city = (f.city ?? '').trim();
  const country = (f.country ?? '').trim();

  if (!company) {
    errors.companyName = 'A company or sender name is required.';
  }
  if (!address) {
    errors.addressLine1 = 'A postal address is required in every marketing email.';
  }
  if (!city) {
    errors.city = 'City is required.';
  }
  if (!country) {
    errors.country = 'Country is required.';
  }
  return errors;
};

export const renderLegalFooter = (f: LegalFooter) => {
  const company = f.companyName || f.legalName || '';
  const address1 = f.addressLine1 || f.postalAddress || '';
  const postalCity = [f.postalCode, f.city].filter(Boolean).join(' ');
  const parts = [company, address1, f.addressLine2, postalCity, f.country]
    .map((part) => String(part || '').trim())
    .filter(Boolean);
  return parts.join(' · ');
};

const FieldError = ({ id, children }: { id?: string; children?: string }) =>
  children ? (
    <p
      id={id}
      className="mt-1 flex items-center gap-1 text-xs font-medium text-destructive"
      role="alert"
    >
      <WarningCircle size={13} className="shrink-0" />
      <span>{children}</span>
    </p>
  ) : null;

const footerErrorsFrom = (error: unknown): FooterErrors => {
  if (!(error instanceof HttpError) || error.status !== 400) return {};
  const raw = Array.isArray(error.error.errors) ? error.error.errors : [];
  const mapped: FooterErrors = {};
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const [prefix, ...message] = entry.split(':');
    const value = message.join(':').trim() || entry;
    const key = prefix.trim().toLowerCase();
    switch (key) {
      case 'companyname':
      case 'legalname':
        mapped.companyName = value;
        break;
      case 'addressline1':
      case 'postaladdress':
        mapped.addressLine1 = value;
        break;
      case 'addressline2':
        mapped.addressLine2 = value;
        break;
      case 'postalcode':
        mapped.postalCode = value;
        break;
      case 'city':
        mapped.city = value;
        break;
      case 'country':
        mapped.country = value;
        break;
      case 'extratext':
        mapped.extraText = value;
        break;
    }
  }
  return mapped;
};

export const LegalFooterSettingsPage = () => {
  const { toast } = useToast();
  const { data } = useGetLegalFooterSettings();
  const updateFooter = useUpdateLegalFooterSettings();
  const [footer, setFooter] = useState<LegalFooter>(INITIAL_FOOTER);
  const [errors, setErrors] = useState<FooterErrors>({});
  const [savedNote, setSavedNote] = useState('');
  const [saveServerError, setSaveServerError] = useState(false);

  const preview = useMemo(() => renderLegalFooter(footer), [footer]);

  useEffect(() => {
    if (data) {
      setFooter({
        companyName: data.companyName || data.legalName || '',
        addressLine1: data.addressLine1 || data.postalAddress || '',
        addressLine2: data.addressLine2 || '',
        postalCode: data.postalCode || '',
        city: data.city || '',
        country: data.country || '',
        extraText: data.extraText || '',
        includeUnsubscribeLink: data.includeUnsubscribeLink ?? true,
        includePreferencesLink: data.includePreferencesLink ?? false,
      });
    }
  }, [data]);

  const update = <K extends keyof LegalFooter>(key: K, value: LegalFooter[K]) => {
    setSavedNote('');
    setSaveServerError(false);
    setFooter((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = validateLegalFooter(footer);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        variant: 'destructive',
        description:
          'Fix the highlighted fields before saving — a postal address is required in every marketing email.',
      });
      return;
    }

    try {
      await updateFooter.mutateAsync({
        companyName: footer.companyName.trim(),
        addressLine1: footer.addressLine1.trim(),
        addressLine2: footer.addressLine2.trim(),
        postalCode: footer.postalCode.trim(),
        city: footer.city.trim(),
        country: footer.country.trim(),
        extraText: footer.extraText.trim(),
        includeUnsubscribeLink: true,
        includePreferencesLink: footer.includePreferencesLink,
      });
      setSavedNote('Saved just now.');
      setSaveServerError(false);
      toast({
        variant: 'success',
        title: 'Legal footer saved',
        description: 'New marketing emails will use these sender details.',
      });
    } catch (error) {
      const apiErrors = footerErrorsFrom(error);
      if (Object.keys(apiErrors).length) {
        setErrors(apiErrors);
        toast({
          variant: 'destructive',
          description: 'Fix the highlighted fields before saving.',
        });
      } else {
        setSaveServerError(true);
        toast({
          variant: 'destructive',
          title: "Couldn't save legal footer",
          description:
            error instanceof Error
              ? error.message
              : 'Something went wrong. Your changes have not been lost — try Save again.',
        });
      }
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[880px] flex-col gap-5 pb-8">
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/settings" className="hover:text-foreground">
            Settings
          </Link>
          <span>/</span>
          <span className="font-medium text-foreground">Legal footer</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Legal footer</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Appended to every marketing email. A missing postal address here is a compliance failure,
          not a style choice.
        </p>
      </div>

      <form onSubmit={save} noValidate className="grid items-start gap-5 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-5">
          {/* Sender Identity */}
          <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
            <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-foreground">Sender identity</h2>

              <div>
                <Label htmlFor="companyName">Company / sender name</Label>
                <Input
                  id="companyName"
                  value={footer.companyName}
                  placeholder="SELISE Digital Platforms AG"
                  onChange={(event) => update('companyName', event.target.value)}
                  aria-invalid={!!errors.companyName}
                  className={cn(errors.companyName && 'border-destructive focus-visible:ring-destructive')}
                />
                <FieldError id="companyNameErr">{errors.companyName}</FieldError>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="addressLine1">Address line 1</Label>
                  <Input
                    id="addressLine1"
                    value={footer.addressLine1}
                    placeholder="Bahnhofstrasse 1"
                    onChange={(event) => update('addressLine1', event.target.value)}
                    aria-invalid={!!errors.addressLine1}
                    className={cn(
                      errors.addressLine1 && 'border-destructive focus-visible:ring-destructive'
                    )}
                  />
                  <FieldError id="addressLine1Err">{errors.addressLine1}</FieldError>
                </div>
                <div>
                  <Label htmlFor="addressLine2">Address line 2 (optional)</Label>
                  <Input
                    id="addressLine2"
                    value={footer.addressLine2}
                    placeholder="Suite 400"
                    onChange={(event) => update('addressLine2', event.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <Label htmlFor="postalCode">Postal code</Label>
                  <Input
                    id="postalCode"
                    value={footer.postalCode}
                    placeholder="8001"
                    onChange={(event) => update('postalCode', event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={footer.city}
                    placeholder="Zurich"
                    onChange={(event) => update('city', event.target.value)}
                    aria-invalid={!!errors.city}
                    className={cn(errors.city && 'border-destructive focus-visible:ring-destructive')}
                  />
                  <FieldError id="cityErr">{errors.city}</FieldError>
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={footer.country}
                    placeholder="Switzerland"
                    onChange={(event) => update('country', event.target.value)}
                    aria-invalid={!!errors.country}
                    className={cn(
                      errors.country && 'border-destructive focus-visible:ring-destructive'
                    )}
                  />
                  <FieldError id="countryErr">{errors.country}</FieldError>
                </div>
              </div>

              <div>
                <Label htmlFor="extraText">Extra text (optional)</Label>
                <Textarea
                  id="extraText"
                  rows={2}
                  value={footer.extraText}
                  placeholder="e.g. a tagline, a second office, VAT number"
                  onChange={(event) => update('extraText', event.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Links Card */}
          <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
            <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-foreground">Links</h2>

              <div className="flex items-center justify-between gap-3 rounded-md border border-neutral-100 bg-neutral-100/60 px-3.5 py-3">
                <div className="flex items-center gap-2.5">
                  <LockSimple size={16} className="shrink-0 text-neutral-500" />
                  <div>
                    <p className="text-sm font-medium text-neutral-900">Unsubscribe link</p>
                    <p className="text-xs text-neutral-500">
                      Always included — this cannot be switched off. It is how a footer save
                      enforces the one-click opt-out.
                    </p>
                  </div>
                </div>
                <Switch checked disabled aria-label="Unsubscribe link locked on" />
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <div>
                  <Label
                    htmlFor="prefsToggle"
                    className="cursor-pointer text-sm font-medium text-neutral-800"
                  >
                    Preferences link
                  </Label>
                  <p className="text-xs text-neutral-500">
                    Adds a link to the contact&apos;s channel preferences alongside Unsubscribe.
                  </p>
                </div>
                <Switch
                  id="prefsToggle"
                  checked={footer.includePreferencesLink}
                  onCheckedChange={(checked) => update('includePreferencesLink', checked)}
                  aria-label="Include preferences link"
                />
              </div>
            </CardContent>
          </Card>

          {saveServerError && (
            <div
              id="saveServerError"
              className="flex items-start gap-2.5 rounded-lg px-3.5 py-3"
              style={{ background: '#FCE4EA' }}
            >
              <WarningCircle
                size={16}
                weight="fill"
                className="mt-0.5 shrink-0"
                style={{ color: '#D80032' }}
              />
              <div className="text-xs" style={{ color: '#8F0022' }}>
                Something went wrong. Your changes have not been lost — try Save again.
              </div>
            </div>
          )}

          <div className="sticky bottom-0 flex items-center justify-between gap-3 flex-wrap bg-[#FAFAFA] pt-3 pb-1">
            <div className="text-xs text-neutral-500">
              {savedNote && <span>{savedNote}</span>}
            </div>
            <Button
              id="btnSave"
              type="submit"
              loading={updateFooter.isPending}
              disabled={updateFooter.isPending}
              className="gap-2"
            >
              {updateFooter.isPending && <CircleNotch size={16} className="animate-spin" />}
              Save changes
            </Button>
          </div>
        </div>

        {/* Live Preview Aside */}
        <aside className="flex flex-col gap-3 lg:sticky lg:top-6" aria-label="Live preview">
          <h2 className="text-lg font-semibold text-foreground">Live preview</h2>
          <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
            <CardContent className="p-5 sm:p-6">
              <div className="rounded-md border border-neutral-100 bg-[#FAFAFA] p-5">
                <p className="mb-3 text-xs text-muted-foreground">— end of email —</p>
                <p id="previewText" className="whitespace-pre-line text-xs leading-relaxed text-neutral-700">
                  {preview || '—'}
                </p>
                {footer.extraText && (
                  <p id="previewExtra" className="mt-2 text-xs text-neutral-700">
                    {footer.extraText}
                  </p>
                )}
                <p className="mt-3 text-xs">
                  <span className="font-medium text-[#0066B2] underline cursor-default">
                    Unsubscribe
                  </span>
                  {footer.includePreferencesLink && (
                    <span id="previewPrefsWrap">
                      {' · '}
                      <span className="font-medium text-[#0066B2] underline cursor-default">
                        Manage preferences
                      </span>
                    </span>
                  )}
                </p>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Rendered from legal footer settings, plus the locked-on unsubscribe link and, if
                enabled, the preferences link.
              </p>
            </CardContent>
          </Card>
        </aside>
      </form>
    </div>
  );
};
